<script lang="ts">
  import { db } from '../../lib/firebase';
  import { authState } from '../../lib/auth.svelte';
  import { collection, query, where, onSnapshot } from 'firebase/firestore';
  import { onMount } from 'svelte';
  import {
    type Ticket,
    type TicketCategory,
    TICKET_CATEGORIES,
    TICKET_PRIORITIES,
    TICKET_STATUSES,
  } from '../../lib/types';

  // Props
  type Props = {
    onSelectTicket: (ticket: Ticket) => void;
  };
  let { onSelectTicket }: Props = $props();

  // State
  let tickets = $state<Ticket[]>([]);
  let loading = $state(true);
  let error = $state('');
  let categoryFilter = $state<TicketCategory | 'all'>('all');

  // Filtered tickets
  let filteredTickets = $derived(
    categoryFilter === 'all'
      ? tickets
      : tickets.filter(t => t.category === categoryFilter)
  );

  onMount(() => {
    if (!authState.user) {
      loading = false;
      return;
    }

    const ticketsRef = collection(db, 'support_tickets');
    // Simple query - just filter by userId, sort client-side to avoid composite index
    const q = query(
      ticketsRef,
      where('userId', '==', authState.user.uid)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        // Map and sort client-side by updatedAt descending
        tickets = snapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data(),
          } as Ticket))
          .sort((a, b) => {
            const aTime = a.updatedAt?.toDate?.() || new Date(a.updatedAt || 0);
            const bTime = b.updatedAt?.toDate?.() || new Date(b.updatedAt || 0);
            return bTime.getTime() - aTime.getTime();
          });
        loading = false;
      },
      (err) => {
        console.error('Error loading tickets:', err);
        error = 'Failed to load tickets';
        loading = false;
      }
    );

    return () => unsubscribe();
  });

  function getStatusInfo(status: string) {
    return TICKET_STATUSES.find(s => s.id === status) || TICKET_STATUSES[0];
  }

  function getPriorityInfo(priority: string) {
    return TICKET_PRIORITIES.find(p => p.id === priority) || TICKET_PRIORITIES[1];
  }

  function formatDate(date: any): string {
    if (!date) return '';
    const d = date.toDate ? date.toDate() : new Date(date);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      const hours = Math.floor(diff / (1000 * 60 * 60));
      if (hours === 0) {
        const minutes = Math.floor(diff / (1000 * 60));
        return minutes <= 1 ? 'Just now' : `${minutes}m ago`;
      }
      return `${hours}h ago`;
    }
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
  }
</script>

<div class="ticket-list-container">
  <div class="list-header">
    <h3>My Tickets</h3>
    <div class="filter-row">
      <label for="category-filter">Filter:</label>
      <select id="category-filter" bind:value={categoryFilter}>
        <option value="all">All Categories</option>
        {#each TICKET_CATEGORIES as cat}
          <option value={cat.id}>{cat.label}</option>
        {/each}
      </select>
    </div>
  </div>

  {#if loading}
    <div class="loading-state">
      <div class="spinner"></div>
      <p>Loading tickets...</p>
    </div>
  {:else if error}
    <div class="error-state">
      <p>{error}</p>
    </div>
  {:else if filteredTickets.length === 0}
    <div class="empty-state">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
      </svg>
      {#if categoryFilter === 'all'}
        <p>No support tickets yet</p>
        <span>Create your first ticket to get help from our team.</span>
      {:else}
        <p>No tickets in this category</p>
        <span>Try selecting a different filter.</span>
      {/if}
    </div>
  {:else}
    <div class="ticket-cards">
      {#each filteredTickets as ticket (ticket.id)}
        <button class="ticket-card" onclick={() => onSelectTicket(ticket)}>
          <div class="ticket-main">
            <div class="ticket-subject">{ticket.subject}</div>
            <div class="ticket-meta">
              <span class="ticket-category">{TICKET_CATEGORIES.find(c => c.id === ticket.category)?.label}</span>
              <span class="ticket-date">{formatDate(ticket.updatedAt)}</span>
            </div>
          </div>
          <div class="ticket-badges">
            <span
              class="status-badge"
              style="background: {getStatusInfo(ticket.status).color}15; color: {getStatusInfo(ticket.status).color}"
            >
              {getStatusInfo(ticket.status).label}
            </span>
            <span
              class="priority-badge"
              style="color: {getPriorityInfo(ticket.priority).color}"
            >
              {getPriorityInfo(ticket.priority).label}
            </span>
          </div>
          <div class="ticket-arrow">
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clip-rule="evenodd" />
            </svg>
          </div>
        </button>
      {/each}
    </div>
  {/if}
</div>

<style>
  .ticket-list-container {
    background: white;
    border-radius: 12px;
    overflow: hidden;
  }

  .list-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .list-header h3 {
    margin: 0;
    font-size: 1rem;
    color: #333;
  }

  .filter-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .filter-row label {
    font-size: 0.85rem;
    color: #666;
  }

  .filter-row select {
    padding: 0.375rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.85rem;
    background: white;
  }

  .loading-state,
  .error-state,
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 1.5rem;
    text-align: center;
  }

  .loading-state .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e5e7eb;
    border-top-color: #667eea;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .loading-state p,
  .error-state p {
    margin: 1rem 0 0;
    color: #666;
  }

  .error-state p {
    color: #dc2626;
  }

  .empty-state svg {
    width: 48px;
    height: 48px;
    color: #9ca3af;
    margin-bottom: 1rem;
  }

  .empty-state p {
    margin: 0;
    font-weight: 500;
    color: #333;
  }

  .empty-state span {
    display: block;
    margin-top: 0.5rem;
    font-size: 0.875rem;
    color: #666;
  }

  .ticket-cards {
    display: flex;
    flex-direction: column;
  }

  .ticket-card {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem 1.25rem;
    border: none;
    background: white;
    text-align: left;
    cursor: pointer;
    transition: background 0.2s;
    border-bottom: 1px solid #f3f4f6;
    width: 100%;
  }

  .ticket-card:hover {
    background: #f9fafb;
  }

  .ticket-card:last-child {
    border-bottom: none;
  }

  .ticket-main {
    flex: 1;
    min-width: 0;
  }

  .ticket-subject {
    font-weight: 500;
    color: #333;
    margin-bottom: 0.25rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ticket-meta {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 0.8rem;
    color: #666;
  }

  .ticket-category {
    background: #f3f4f6;
    padding: 0.125rem 0.5rem;
    border-radius: 4px;
  }

  .ticket-badges {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.25rem;
    flex-shrink: 0;
  }

  .status-badge {
    font-size: 0.75rem;
    font-weight: 500;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
  }

  .priority-badge {
    font-size: 0.7rem;
    font-weight: 500;
  }

  .ticket-arrow {
    color: #9ca3af;
    flex-shrink: 0;
  }

  .ticket-arrow svg {
    width: 20px;
    height: 20px;
  }

  .ticket-card:hover .ticket-arrow {
    color: #667eea;
  }

  @media (max-width: 480px) {
    .list-header {
      flex-direction: column;
      gap: 0.75rem;
      align-items: flex-start;
    }

    .ticket-badges {
      display: none;
    }
  }
</style>
